import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quotes, Check } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogOverlay, DialogPortal } from "./ui/dialog";
import ArtDecoDivider from "./ArtDecoDivider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
    { value: "", label: "—" },
    { value: "cocktail_party", label: "Cocktail Party" },
    { value: "wedding", label: "Wedding" },
    { value: "casual_gathering", label: "Casual Gathering" },
    { value: "corporate", label: "Corporate" },
    { value: "birthday", label: "Birthday" },
    { value: "holiday_party", label: "Holiday Party" },
];

const EVENT_LABELS = Object.fromEntries(EVENT_TYPES.map((e) => [e.value, e.label]));

function StarDisplay({ rating, size = 16 }) {
    return (
        <div className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
            {[1, 2, 3, 4, 5].map((n) => (
                <Star
                    key={n}
                    size={size}
                    weight={n <= rating ? "fill" : "regular"}
                    className={n <= rating ? "text-gold" : "text-white/20"}
                />
            ))}
        </div>
    );
}

function StarInput({ value, onChange, testid = "rating-input" }) {
    const [hover, setHover] = useState(0);
    const shown = hover || value;
    return (
        <div className="inline-flex items-center gap-1" data-testid={testid}>
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => onChange(n)}
                    aria-label={`${n} star${n !== 1 ? "s" : ""}`}
                    data-testid={`star-${n}`}
                    className="p-1.5 transition-transform duration-150 hover:scale-110"
                >
                    <Star
                        size={28}
                        weight={n <= shown ? "fill" : "regular"}
                        className={n <= shown ? "text-gold" : "text-white/30"}
                    />
                </button>
            ))}
        </div>
    );
}

function ReviewCard({ review }) {
    return (
        <div
            data-testid={`review-card-${review.id}`}
            className="relative bg-obsidian-soft border border-white/10 hover:border-gold/30 transition-colors duration-500 p-7 md:p-8 h-full flex flex-col"
        >
            <Quotes
                size={28}
                weight="fill"
                className="text-gold/40 absolute top-5 right-5"
                aria-hidden="true"
            />
            <StarDisplay rating={review.rating} />
            <p className="font-display italic text-white/85 text-lg leading-relaxed mt-5 mb-6 flex-1">
                "{review.comment}"
            </p>
            <div className="pt-4 border-t border-white/5">
                <div className="text-white text-sm tracking-wide">{review.name}</div>
                {(review.event_type || review.location) && (
                    <div className="text-white/40 text-xs mt-1 font-sans">
                        {[EVENT_LABELS[review.event_type], review.location].filter(Boolean).join(" · ")}
                    </div>
                )}
            </div>
        </div>
    );
}

function ReviewDialog({ open, onOpenChange, onSubmitted }) {
    const [form, setForm] = useState({
        name: "",
        rating: 0,
        comment: "",
        event_type: "",
        location: "",
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [serverError, setServerError] = useState("");

    const reset = () => {
        setForm({ name: "", rating: 0, comment: "", event_type: "", location: "" });
        setErrors({});
        setServerError("");
        setSuccess(false);
        setSubmitting(false);
    };

    const handleOpenChange = (next) => {
        if (!next) reset();
        onOpenChange(next);
    };

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!form.name.trim()) errs.name = "Required";
        if (!form.rating || form.rating < 1 || form.rating > 5) errs.rating = "Pick a rating";
        if (!form.comment.trim()) errs.comment = "Please share a few words";
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setSubmitting(true);
        setServerError("");
        try {
            await axios.post(`${API}/reviews`, {
                name: form.name.trim(),
                rating: form.rating,
                comment: form.comment.trim(),
                event_type: form.event_type || null,
                location: form.location.trim() || null,
            });
            setSuccess(true);
            if (onSubmitted) onSubmitted();
        } catch (err) {
            const m = err?.response?.data?.detail;
            setServerError(typeof m === "string" ? m : "Could not submit. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogPortal>
                <DialogOverlay className="bg-black/80 backdrop-blur-sm" />
                <DialogContent
                    className="sm:max-w-lg bg-[#0A0A0A] border border-gold/30 text-white p-0 [&>button]:hidden max-h-[90vh] overflow-y-auto"
                    data-testid="review-dialog"
                >
                    {success ? (
                        <div className="p-10 text-center">
                            <Check size={56} weight="thin" className="text-gold mx-auto mb-4" />
                            <div className="text-gold text-xs tracking-overline uppercase mb-2">
                                Submitted
                            </div>
                            <h3 className="font-display italic text-white text-3xl mb-4">
                                Thank you
                            </h3>
                            <p className="text-white/70 text-sm font-light leading-relaxed mb-8">
                                Your review has been received and will appear on the site
                                once it's been reviewed.
                            </p>
                            <button
                                type="button"
                                onClick={() => handleOpenChange(false)}
                                className="btn-gold-outline"
                                data-testid="review-success-close"
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="p-8 md:p-10" data-testid="review-form" noValidate>
                            <div className="text-gold text-xs tracking-overline uppercase mb-2 text-center">
                                Leave a Review
                            </div>
                            <h3 className="font-display italic text-white text-3xl mb-1 text-center">
                                How was the evening?
                            </h3>
                            <p className="text-white/50 text-xs font-light text-center mb-8">
                                Reviews are read before they appear on the site.
                            </p>

                            <div className="mb-5 text-center">
                                <label className="label-overline mb-2">Your Rating · </label>
                                <StarInput
                                    value={form.rating}
                                    onChange={(v) => setField("rating", v)}
                                />
                                {errors.rating && (
                                    <div className="text-xs text-red-400 mt-1 font-sans">
                                        {errors.rating}
                                    </div>
                                )}
                            </div>

                            <div className="mb-5">
                                <label className="label-overline">Your Name ·</label>
                                <input
                                    type="text"
                                    className="input-dark"
                                    maxLength={80}
                                    value={form.name}
                                    onChange={(e) => setField("name", e.target.value)}
                                    data-testid="review-name"
                                />
                                {errors.name && (
                                    <div className="text-xs text-red-400 mt-1 font-sans">{errors.name}</div>
                                )}
                            </div>

                            <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-overline">Event Type (optional)</label>
                                    <select
                                        className="input-dark cursor-pointer"
                                        value={form.event_type}
                                        onChange={(e) => setField("event_type", e.target.value)}
                                        data-testid="review-event-type"
                                    >
                                        {EVENT_TYPES.map((et) => (
                                            <option key={et.value} value={et.value}>{et.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="label-overline">Location (optional)</label>
                                    <input
                                        type="text"
                                        className="input-dark"
                                        maxLength={120}
                                        placeholder="City or venue"
                                        value={form.location}
                                        onChange={(e) => setField("location", e.target.value)}
                                        data-testid="review-location"
                                    />
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="label-overline">Your Review ·</label>
                                <textarea
                                    rows={5}
                                    maxLength={1200}
                                    className="input-dark resize-y"
                                    placeholder="Tell us about your evening…"
                                    value={form.comment}
                                    onChange={(e) => setField("comment", e.target.value)}
                                    data-testid="review-comment"
                                />
                                {errors.comment && (
                                    <div className="text-xs text-red-400 mt-1 font-sans">{errors.comment}</div>
                                )}
                            </div>

                            {serverError && (
                                <div className="mb-4 px-4 py-3 border border-red-500/30 bg-red-500/10 text-red-300 text-sm font-sans">
                                    {serverError}
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    type="button"
                                    onClick={() => handleOpenChange(false)}
                                    className="btn-gold-outline flex-1"
                                    data-testid="review-cancel"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-gold flex-1 disabled:opacity-50"
                                    data-testid="review-submit"
                                >
                                    {submitting ? "Sending…" : "Submit Review"}
                                </button>
                            </div>
                        </form>
                    )}
                </DialogContent>
            </DialogPortal>
        </Dialog>
    );
}

export default function ReviewsSection() {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    const fetchReviews = async () => {
        try {
            const res = await axios.get(`${API}/reviews/approved?limit=9`);
            setReviews(res.data || []);
        } catch {
            setReviews([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, []);

    const hasReviews = reviews.length > 0;

    return (
        <section
            id="reviews"
            data-testid="reviews-section"
            className="bg-obsidian-deep border-t border-white/5 py-24 px-6 lg:px-12"
        >
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                    <div className="text-gold text-xs tracking-overline uppercase mb-3">
                        From The Bar
                    </div>
                    <h2 className="font-display text-white text-4xl md:text-5xl mb-2">
                        Kind <em className="gold-italic">words</em>
                    </h2>
                    <ArtDecoDivider />
                    <p className="text-white/55 max-w-xl mx-auto font-light text-sm md:text-base">
                        A few notes from recent evenings. Worked with us? We'd love to hear about it.
                    </p>
                </div>

                {loading ? (
                    <div className="text-center text-white/30 text-sm font-sans py-12" data-testid="reviews-loading">
                        Loading…
                    </div>
                ) : hasReviews ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reviews-grid">
                        {reviews.map((r) => (
                            <ReviewCard key={r.id} review={r} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16 border border-dashed border-white/10" data-testid="reviews-empty">
                        <p className="text-white/40 font-display italic text-lg md:text-xl mb-2">
                            Be the first to leave a review.
                        </p>
                        <p className="text-white/30 text-sm font-sans">
                            Just worked with us? Drop a few words below.
                        </p>
                    </div>
                )}

                <div className="text-center mt-12">
                    <button
                        type="button"
                        onClick={() => setDialogOpen(true)}
                        className="btn-gold"
                        data-testid="reviews-leave-cta"
                    >
                        Leave a Review
                    </button>
                </div>
            </div>

            <ReviewDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                onSubmitted={fetchReviews}
            />
        </section>
    );
}
